import { normalizeCreditNoteForPdf } from './normalize/normalizeCreditNoteForPdf';
import { normalizeDeliveryNoteForPdf } from './normalize/normalizeDeliveryNoteForPdf';
import { normalizeMissingNoteForPdf } from './normalize/normalizeMissingNoteForPdf';
import { normalizeOrderForPdf } from './normalize/normalizeOrderForPdf';
import { printPdfTemplate } from './pdfPrintingService';
import { PDF_TEMPLATE_IDS } from './templates/registry';

/** Matches legacy order shape (`createdAt` present) vs flattened billing on the order. */
export function orderUsesLegacyOwnerShape(order) {
    return order?.createdAt !== undefined;
}

/**
 * @param {Record<string, unknown>} order
 * @param {'order' | 'delivery' | 'missing'} [variant]
 * @param {{ extraInfoLookup?: Array<{ owner: string; extraInfo: object }> }} [options]
 */
export async function printOrderSummaryPdf(order, variant = 'order', options = {}) {
    if (!order) {
        throw new Error('No order loaded');
    }
    const old = orderUsesLegacyOwnerShape(order);
    const { extraInfoLookup } = options;
    const data =
        variant === 'delivery'
            ? normalizeDeliveryNoteForPdf(order, { old, extraInfoLookup })
            : variant === 'missing'
                ? normalizeMissingNoteForPdf(order, { old, extraInfoLookup })
                : normalizeOrderForPdf(order, { old, variant });
    const templateByVariant = {
        order: PDF_TEMPLATE_IDS.ORDER_SUMMARY,
        delivery: PDF_TEMPLATE_IDS.DELIVERY_NOTE,
        missing: PDF_TEMPLATE_IDS.MISSING_NOTE,
    };
    const titles = {
        order: 'Print order',
        delivery: 'Print delivery note',
        missing: 'Print missing note',
    };
    await printPdfTemplate(templateByVariant[variant], { data }, { title: titles[variant] || 'Print' });
}

/** Invoice-focused PDF (same order payload; template shows invoice header/status). */
export async function printInvoiceSummaryPdf(order) {
    if (!order) {
        throw new Error('No order loaded');
    }
    const old = orderUsesLegacyOwnerShape(order);
    const data = normalizeOrderForPdf(order, { old, variant: 'order' });
    await printPdfTemplate(PDF_TEMPLATE_IDS.INVOICE_SUMMARY, { data }, { title: 'Print invoice' });
}

/**
 * @param {Record<string, unknown>} order
 * @param {Record<string, unknown>} creditNote — document from `order.creditNotes`
 */
export async function printCreditNotePdf(order, creditNote) {
    if (!order) {
        throw new Error('No order loaded');
    }
    if (!creditNote) {
        throw new Error('No credit note loaded');
    }
    const old = orderUsesLegacyOwnerShape(order);
    const data = normalizeCreditNoteForPdf(order, creditNote, { old });
    await printPdfTemplate(PDF_TEMPLATE_IDS.CREDIT_NOTE, { data }, { title: 'Print credit note' });
}
